import { Plus, Volume2, VolumeX, X } from "lucide-react";

export type TrackRow = {
  id: string;
  label: string;
  type: string;
  volume: number;
  setVolume: (v: number) => void;
  color: string;
  isDeletable: boolean;
  volumeKindLabel: string;
};

export type TrackHeaderProps = {
  masterVolume: number;
  trackRows: TrackRow[];
  canAddAmbientTrack: boolean;
  canAddSfxTrack: boolean;
  onMasterMuteToggle: () => void;
  onMasterVolumeChange: (v: number) => void;
  onTrackMuteToggle: (volumeKindLabel: string, currentVol: number, setVol: (v: number) => void) => void;
  onTrackVolumeChangeCommit: (volumeKindLabel: string, newVolume: number) => void;
  onRemoveTrack: (id: string) => void;
  onAddAmbientTrack: () => void;
  onAddSfxTrack: () => void;
};

export function TrackHeader({
  masterVolume,
  trackRows,
  canAddAmbientTrack,
  canAddSfxTrack,
  onMasterMuteToggle,
  onMasterVolumeChange,
  onTrackMuteToggle,
  onTrackVolumeChangeCommit,
  onRemoveTrack,
  onAddAmbientTrack,
  onAddSfxTrack,
}: TrackHeaderProps) {
  return (
    <div className="shrink-0 z-20 border-r border-[#e2e8f0]" style={{ width: 160 }}>
      {/* Ruler placeholder row — master volume leveler */}
      <div className="h-[28px] bg-[#f8fafc] border-b border-[#e2e8f0] flex items-center px-4 gap-2">
        <button
          type="button"
          onClick={onMasterMuteToggle}
          className="shrink-0 text-[#0f172b] hover:text-[#007aff]"
          aria-label={masterVolume === 0 ? "Réactiver le son" : "Couper le son"}
        >
          {masterVolume === 0
            ? <VolumeX className="h-4 w-4" />
            : <Volume2 className="h-4 w-4" />}
        </button>
        <input
          type="range"
          min="0"
          max="1"
          step="0.02"
          value={masterVolume}
          onChange={(e) => onMasterVolumeChange(parseFloat(e.target.value))}
          className="w-full h-1 bg-[#e2e8f0] rounded-lg appearance-none cursor-pointer accent-[#0f172b]"
          style={{
            background: `linear-gradient(to right, #0f172b 0%, #0f172b ${masterVolume * 100}%, #e2e8f0 ${masterVolume * 100}%, #e2e8f0 100%)`
          }}
        />
      </div>

      {/* Track label rows */}
      {trackRows.map(({ id, label, type, volume, setVolume, color, isDeletable, volumeKindLabel }) => (
        <div
          key={id}
          className="h-[88px] flex flex-col justify-center px-4 border-b border-[#e2e8f0] bg-white"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <span className="block text-[13px] font-semibold text-[#0f172b] leading-tight truncate">{label}</span>
              <span className="block text-[11px] text-[#94a3b8] leading-tight mt-0.5">{type}</span>
            </div>
            {isDeletable && (
              <button
                type="button"
                onClick={() => onRemoveTrack(id)}
                className="shrink-0 text-[#94a3b8] hover:text-[#c8009c] transition-colors"
                aria-label={`Supprimer la piste ${label}`}
                title={`Supprimer la piste ${label}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => onTrackMuteToggle(volumeKindLabel, volume, setVolume)}
              className="shrink-0 text-[#0f172b] hover:text-[#007aff]"
              aria-label={volume === 0 ? "Réactiver la piste" : "Couper la piste"}
            >
              {volume === 0
                ? <VolumeX className="h-3.5 w-3.5" />
                : <Volume2 className="h-3.5 w-3.5" />}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.02"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              onMouseUp={(e) => onTrackVolumeChangeCommit(volumeKindLabel, parseFloat((e.target as HTMLInputElement).value))}
              onTouchEnd={(e) => onTrackVolumeChangeCommit(volumeKindLabel, parseFloat((e.target as HTMLInputElement).value))}
              className="w-20 h-1 bg-[#e2e8f0] rounded-lg appearance-none cursor-pointer"
              style={{
                accentColor: color,
                background: `linear-gradient(to right, ${color} 0%, ${color} ${volume * 100}%, #e2e8f0 ${volume * 100}%, #e2e8f0 100%)`
              }}
            />
          </div>
        </div>
      ))}

      {/* Add-track buttons row */}
      {(canAddAmbientTrack || canAddSfxTrack) && (
        <div className="flex flex-col gap-1 px-4 py-3 border-b border-[#e2e8f0] bg-[#f8fafc]">
          {canAddAmbientTrack && (
            <button
              type="button"
              onClick={onAddAmbientTrack}
              className="inline-flex items-center gap-1 text-[12px] font-medium text-[#04a404] hover:text-[#037803] transition-colors"
              title="Ajouter une piste ambiance"
            >
              <Plus className="h-3 w-3" />
              Piste ambiance
            </button>
          )}
          {canAddSfxTrack && (
            <button
              type="button"
              onClick={onAddSfxTrack}
              className="inline-flex items-center gap-1 text-[12px] font-medium text-[#c8009c] hover:text-[#a8008a] transition-colors"
              title="Ajouter une piste d'effets sonores"
            >
              <Plus className="h-3 w-3" />
              Piste effets
            </button>
          )}
        </div>
      )}
    </div>
  );
}
